<!-- 
	@component
	Simple text-only select component built on the `<Select>` primitive component.

	Usage:
	```svelte
	<TextMultiSelect 
		bind:selectedIds={[selectedOptionA, selectedOptionB]} 
		options={['sponge', 'star', 'potato']}
	/>

	<TextMultiSelect 
		bind:selectedId={[selectedOptionB]}
		options={{sponge: 'SPONGEBOB', star: 'PATRICK'}}
	/>
	```
 -->
<script lang="ts">
	import { ChevronDown, ChevronUp, Check } from '@steeze-ui/heroicons';
	import { Icon } from '@steeze-ui/svelte-icon';
	import Select from './Select.svelte';
	import SelectItem from './SelectItem.svelte';
	import type { TextSelectOptions, TextSelectTuple } from './select';
	import classNames from 'classnames';

	/**
	 * If passing a string[], it will treat the values as both the option ID and label.
	 *
	 * Pass a Record<Id (string), Label (string)> if you want to decouple the data &
	 * presentation values.
	 */
	export let options: string[] | TextSelectOptions;
	export let isOpen = false;
	export let multiple = true;
	export let mandatory = false;
	export let selectedIds: string[] = [];
	export let prompt = 'Select an option';
	export let displayLabel: string;

	// restructured select options to map easily
	let items: TextSelectTuple[];
	$: items =
		options instanceof Array
			? options.map((option) => [option, option])
			: Object.entries(options);

	// Concat all selected elements to create a label
	$: selectedLabel = items
		.filter(([id]) => selectedIds.includes(id))
		.map(([_, label]) => label)
		.join(', ');

	const handleSelected = () => {
		isOpen = false;
	};
</script>

<Select {multiple} {mandatory} bind:selected={selectedIds} bind:isOpen>
	<div
		slot="input"
		class="flex w-fit min-w-[18rem] cursor-pointer justify-between gap-2 rounded-full border-2 px-4 py-2"
	>
		<span class="flex items-center gap-1 capitalize">
			{#if selectedLabel}
				{displayLabel}: {selectedLabel}
			{:else}
				{prompt}
			{/if}
		</span>
		<span class="text-accent">
			<Icon src={isOpen ? ChevronUp : ChevronDown} size="24px" />
		</span>
	</div>
	<div class="list mt-2 w-max min-w-[18rem] overflow-hidden rounded-3xl">
		{#each items as [id, label]}
			<SelectItem {id} on:selected on:selected={handleSelected}>
				<div class="flex items-center gap-3 px-4">
					<div class="h-5 w-5 shrink-0">
						{#if label === selectedLabel}
							<Icon src={Check} size="20px" />
						{/if}
					</div>
					<div
						class={classNames(
							'py-2 text-sm capitalize',
							label === selectedLabel
								? 'font-semibold text-base-content'
								: 'font-normal text-accent'
						)}
					>
						{label}
					</div>
				</div>
			</SelectItem>
		{/each}
	</div>
</Select>
